import type { FieldDef } from "@/lib/api/types";

/** A form input value. Numbers are held as strings (controlled inputs); booleans
 * as booleans. Coerced to typed JSON at submit via `coerceToBlob`. */
export type FormValue = string | boolean;

const NUMERIC_TYPES = new Set(["integer", "number", "ratio", "duration"]);

/**
 * Initial value for a field's input.
 *
 * A **required** choice starts on its first option rather than blank. Left
 * empty it is silently dropped from the blob (see `coerceToBlob`) and comes
 * back as a server violation about a field the operator never knew existed —
 * which is exactly how a race with no ranking rule failed to save.
 */
export function defaultValueFor(field: FieldDef): FormValue {
  if (field.type === "boolean") return false;
  if (field.required && field.type === "enum" && field.options?.length) {
    return String(field.options[0].value);
  }
  return "";
}

function coerceScalar(field: FieldDef, raw: string): unknown {
  if (field.type === "enum") {
    // Enum options may carry numeric values (e.g. 301) — return the option's
    // typed value so the blob matches the definition, not the string.
    const match = field.options?.find((o) => String(o.value) === raw);
    return match ? match.value : raw;
  }
  if (NUMERIC_TYPES.has(field.type)) {
    const n = Number(raw);
    return Number.isNaN(n) ? raw : n; // NaN → keep raw so the backend flags it
  }
  return raw;
}

/**
 * Build the JSON blob a fixture endpoint expects from a field-set + form values.
 * Booleans are always included; other empty values are omitted so a required
 * field surfaces as a backend violation rather than an empty string.
 */
export function coerceToBlob(
  fields: FieldDef[],
  values: Record<string, FormValue>,
): Record<string, unknown> {
  const blob: Record<string, unknown> = {};
  for (const field of fields) {
    const raw = values[field.key];
    if (field.type === "boolean") {
      blob[field.key] = raw === true;
      continue;
    }
    if (raw === undefined || raw === "") continue;
    blob[field.key] = coerceScalar(field, String(raw));
  }
  return blob;
}
